/**
 * Pure view model and copy for the per-pool notifications screen at
 * `/pools?poolId=<id>&view=notifications`.
 *
 * `isOverridden` arrives from `get-pool` and is never inferred by comparing
 * a row's values against the account settings; an override is explicit and
 * sticky (NOTIF-14), so a row whose values happen to match the account
 * default is still an override if the server says so.
 *
 * This module is pure: no React import, no DOM, no fetch, no hook.
 */
import { isPoolNoAccessCode } from "../../errors/poolAccess";
import {
  ALERT_GROUP_HEADERS,
  ALERT_GROUP_ORDER,
  ALERT_KIND_LABELS,
  CHANNEL_COLUMNS,
  type typeAlertGroup,
  type typeAlertKind,
  type typeChannel,
} from "../notifications/alertKindLabels";
import { WEB_SETTINGS_CHANNELS } from "../notifications/webSettingsChannels";

export const POOL_NOTIFICATIONS_BACK_LABEL = "Back to pool";
export const POOL_NOTIFICATIONS_INTRO =
  "Anything you don't change here follows your account settings.";
export const POOL_NOTIFICATIONS_FOLLOWING_CAPTION =
  "Following your global setting.";
export const POOL_NOTIFICATIONS_RESET_LABEL = "Reset to default";
export const POOL_NOTIFICATIONS_LOADING_LABEL = "Loading notification settings";
export const POOL_NOTIFICATIONS_FAILED_MESSAGE =
  "We could not load these notification settings.";
export const POOL_NOTIFICATIONS_RETRY_LABEL = "Try again";
export const POOL_NOTIFICATIONS_EMPTY_MESSAGE =
  "There are no pool alerts to set here.";

export function poolNotificationsTitle(poolName: string): string {
  return `Notifications for ${poolName}`;
}

export type typePoolAlertChannelInput = {
  isEnabled: boolean;
  isLocked: boolean;
};

export type typePoolAlertRowInput = {
  alertKind: typeAlertKind;
  group: typeAlertGroup;
  isOverridden: boolean;
  email: typePoolAlertChannelInput;
  inApp: typePoolAlertChannelInput;
  phone: typePoolAlertChannelInput;
};

export type typePoolAlertCell = {
  channel: typeChannel;
  label: string;
  isEnabled: boolean;
  isLocked: boolean;
};

export type typePoolAlertRowModel = {
  alertKind: typeAlertKind;
  label: string;
  isOverridden: boolean;
  cells: typePoolAlertCell[];
};

export type typePoolAlertGroupModel = {
  group: typeAlertGroup;
  header: string;
  rows: typePoolAlertRowModel[];
};

export type typePoolNotificationsState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noAccess" }
  | { phase: "empty"; poolName: string }
  | { phase: "ready"; poolName: string; groups: typePoolAlertGroupModel[] };

function buildCells(row: typePoolAlertRowInput): typePoolAlertCell[] {
  const byChannel: Record<typeChannel, typePoolAlertChannelInput> = {
    email: row.email,
    inApp: row.inApp,
    phone: row.phone,
  };
  return WEB_SETTINGS_CHANNELS.map((channel) => {
    const column = CHANNEL_COLUMNS.find((entry) => entry.channel === channel);
    return {
      channel,
      label: column?.label ?? channel,
      isEnabled: byChannel[channel].isEnabled,
      isLocked: byChannel[channel].isLocked,
    };
  });
}

function buildGroups(
  alerts: readonly typePoolAlertRowInput[],
): typePoolAlertGroupModel[] {
  return ALERT_GROUP_ORDER.map((group) => {
    const rows = alerts
      .filter((row) => row.group === group)
      .map((row) => ({
        alertKind: row.alertKind,
        label: ALERT_KIND_LABELS[row.alertKind],
        isOverridden: row.isOverridden,
        cells: buildCells(row),
      }));
    return { group, header: ALERT_GROUP_HEADERS[group], rows };
  }).filter((groupModel) => groupModel.rows.length > 0);
}

export function resolvePoolNotificationsState(input: {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
  poolName: string | null;
  alerts: readonly typePoolAlertRowInput[] | null;
}): typePoolNotificationsState {
  if (isPoolNoAccessCode(input.errorCode)) return { phase: "noAccess" };
  if (input.isTransportError || input.errorCode !== null) {
    return { phase: "failed" };
  }
  if (input.isLoading) return { phase: "loading" };
  if (input.poolName === null || input.alerts === null) {
    return { phase: "failed" };
  }
  if (input.alerts.length === 0) {
    return { phase: "empty", poolName: input.poolName };
  }
  return {
    phase: "ready",
    poolName: input.poolName,
    groups: buildGroups(input.alerts),
  };
}
